import React, { FC, useState } from 'react';
import { connect } from 'react-redux';
import { Alert, Button, Empty, Modal, Popconfirm, Spin } from 'antd';
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { complianceTypesRestCrudThunks } from '../../thunks/compliance_types_thunks';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { withUserType } from '../withUserType';
import { ComplianceType } from '../../types/ComplianceTypes';
import useMountEffect from '../../hooks/useMountEffect';
import ComplianceTypeFormModal from './ComplianceTypeFormModal';

const COMPLIANCE_TYPES_INDEX_TARGET_COLLECTION = 'complianceTypesIndex';
// The request + document forms feed their compliance-type OWAsyncSelect from this
// shared collection. Refetch it whenever a type is created/edited/deleted here so
// the dropdown reflects the change the next time a form opens.
const TYPES_DROPDOWN_TARGET_COLLECTION = 'complianceTypesDropdown';

interface ComplianceTypesModalProps {
	visible: boolean;
	// Falls back to the logged-in user's role (session.userType, injected by
	// withUserType) when not explicitly provided.
	roleType?: string;
	onCancel: () => void;
	complianceTypes: ComplianceType[];
	fetching: boolean;
	fetchErrors: string[];
	fetchComplianceTypes: () => Promise<any>;
	refetchTypesDropdown: () => void;
	deleteComplianceType: (complianceType: ComplianceType) => Promise<any>;
}

const ComplianceTypesModal: FC<ComplianceTypesModalProps> = ({
	visible,
	roleType,
	userType,
	onCancel,
	complianceTypes,
	fetching,
	fetchErrors,
	fetchComplianceTypes,
	refetchTypesDropdown,
	deleteComplianceType,
}): React.ReactElement => {
	const role = roleType || userType;
	const [formModalOpen, setFormModalOpen] = useState(false);
	const [editingComplianceType, setEditingComplianceType] = useState(null);

	useMountEffect(() => {
		fetchComplianceTypes();
	});

	const openNewTypeModal = () => {
		setEditingComplianceType(null);
		setFormModalOpen(true);
	};

	const closeFormModal = () => setFormModalOpen(false);

	const handleFormSuccess = () => {
		setFormModalOpen(false);
		fetchComplianceTypes();
		refetchTypesDropdown();
	};

	const handleDelete = (complianceType) => {
		deleteComplianceType(complianceType).then(() => {
			fetchComplianceTypes();
			refetchTypesDropdown();
		});
	};

	const renderFlag = (enabled: boolean, enabledLabel: string, disabledLabel: string) => (
		<span className="footnoteregular text-character-secondary">
			{enabled ? (
				<>
					<CheckOutlined className="mr-1" />
					{enabledLabel}
				</>
			) : (
				<>
					<CloseOutlined className="mr-1" />
					{disabledLabel}
				</>
			)}
		</span>
	);

	const renderComplianceTypeRow = (complianceType: ComplianceType) => {
		const handleEditClick = () => {
			setEditingComplianceType(complianceType);
			setFormModalOpen(true);
		};
		const handleDeleteConfirm = () => handleDelete(complianceType);
		return (
			<div
				key={complianceType.id}
				className="flex flex-row items-center justify-between border-0 border-b border-solid border-grey-4 py-4"
			>
				<div className="flex flex-col gap-1">
					<div className="h5medium">{complianceType.name}</div>
					<div className="flex flex-row items-center gap-3">
						{renderFlag(complianceType.allowSampleUpload, 'Sample upload', 'No Sample upload')}
						<span className="footnoteregular text-character-secondary">
							{complianceType.requiresUniqueDocument ? 'Unique document' : 'General document'}
						</span>
						<span className="footnoteregular text-character-secondary">
							{complianceType.isLocationSpecific ? 'Location specific' : 'All Locations'}
						</span>
					</div>
				</div>
				<div className="flex flex-row items-center gap-2">
					<Button icon={<EditOutlined />} onClick={handleEditClick} />
					<Popconfirm
						title={`Delete "${complianceType.name}"?`}
						okText="Delete"
						onConfirm={handleDeleteConfirm}
					>
						<Button icon={<DeleteOutlined />} />
					</Popconfirm>
				</div>
			</div>
		);
	};

	const renderBody = () => {
		if (fetching && complianceTypes.length === 0) {
			return (
				<div className="flex justify-center py-12">
					<Spin />
				</div>
			);
		}
		if (fetchErrors.length > 0) {
			return <Alert type="error" message={fetchErrors.join(', ')} />;
		}
		if (complianceTypes.length === 0) {
			return (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description="No compliance types yet. Create one to start sending compliance requests."
				/>
			);
		}
		return <div>{complianceTypes.map(renderComplianceTypeRow)}</div>;
	};

	const footer = (
		<div className="flex flex-row items-center justify-between">
			<Button onClick={openNewTypeModal}>+ COMPLIANCE TYPE</Button>
			<Button type="primary" onClick={onCancel}>
				DONE
			</Button>
		</div>
	);

	return (
		<Modal
			visible={visible}
			title={`Compliance Types (${complianceTypes.length})`}
			onCancel={onCancel}
			footer={footer}
			width={587}
		>
			{renderBody()}
			{formModalOpen && (
				<ComplianceTypeFormModal
					visible={formModalOpen}
					roleType={role}
					editingComplianceType={editingComplianceType}
					onCancel={closeFormModal}
					onSuccess={handleFormSuccess}
				/>
			)}
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	complianceTypes: getRecordsForTargetCollection(
		state.compliance_types,
		COMPLIANCE_TYPES_INDEX_TARGET_COLLECTION
	),
	fetching: state.compliance_types.fetching,
	fetchErrors: state.compliance_types.fetchErrors,
});

const mapDispatchToProps = (dispatch, ownProps) => {
	const role = ownProps.roleType || ownProps.userType;
	return {
		fetchComplianceTypes: () =>
			dispatch(
				complianceTypesRestCrudThunks(role).read({}, COMPLIANCE_TYPES_INDEX_TARGET_COLLECTION)
			),
		refetchTypesDropdown: () =>
			dispatch(complianceTypesRestCrudThunks(role).read({}, TYPES_DROPDOWN_TARGET_COLLECTION)),
		deleteComplianceType: (complianceType) =>
			dispatch(complianceTypesRestCrudThunks(role).deleteImmediate(complianceType, 'id')),
	};
};

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(ComplianceTypesModal));
